import { CreateWorksheetSelector } from 'Components/Worksheets/Core/types';
import { Phase, PreparationMethod, StateOfMaterial } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import { ISieveSize, SieveSizeId } from 'Data/ViewModels/Equipment/ISieveSize';
import { pipe } from 'Lib/Hooks/Selectors';
import { uniqBy as deprecatedUniqBy } from 'lodash';
import { isNil } from 'ramda';

/**
 * @deprecated Use ramda not lodash
 */
const uniqBy = deprecatedUniqBy;

export const getSeparationPath = (separationIndex: any) =>
  `WholePortion.Separation${'.FinePortion.Subspecimen.Separation'.repeat(separationIndex)}`;

export const getSeparationSieveSizeIds = (worksheet: any) => {
  return [...generateSeparationSieveIds(worksheet)];
};

export const getNumberOfSeparations = (worksheet: any) => {
  return getSeparationSieveSizeIds(worksheet).length;
};

function* generateSeparationSieveIds(worksheet: any) {
  let separation = worksheet.data.WholePortion.Separation;
  while (separation) {
    yield separation?.SeparatingSieveSizeId;
    separation = separation?.FinePortion?.Subspecimen?.Separation;
  }
}

export const getSievingSieveSizeIds = (worksheet: any) => {
  return uniqBy([...generateSievingSieveSizeIds(worksheet)], (s: any) => s.Id);
};

function* generateSievingSieveSizeIds(worksheet: any) {
  yield* worksheet.data.WholePortion.Sieving?.Records?.map((r: any) => r.SieveSize);
  let separation = worksheet.data.WholePortion.Separation;
  while (separation) {
    yield* separation?.CoarsePortion?.Sieving?.Records?.map((r: any) => r.SieveSize);
    yield* separation?.FinePortion?.Subspecimen?.Sieving?.Records?.map((r: any) => r.SieveSize);
    separation = separation?.FinePortion?.Subspecimen?.Separation;
  }
}

/**
 * Returns true if a is a phase that occurs earlier than b
 */
export const isPhaseEarlierThan = (a: Phase, b: Phase) => {
  let orderedPhases: Array<Phase> = [
    Phase.WholePortion,
    Phase.AfterFirstSeparation,
    Phase.AfterSecondSeparation,
    Phase.AfterThirdSeparation,
  ];
  return (
    orderedPhases.includes(a) &&
    orderedPhases.includes(b) &&
    orderedPhases.findIndex((p) => p === a) < orderedPhases.findIndex((p) => p === b)
  );
};

export const getPhaseFollowingSeparation = (separationIndex: number) =>
  [Phase.AfterFirstSeparation, Phase.AfterSecondSeparation, Phase.AfterThirdSeparation][separationIndex];

export const getLabelForPortionState = (portionState: StateOfMaterial) => {
  switch (portionState) {
    case StateOfMaterial.Moist:
      return 'Moist';
    case StateOfMaterial.OvenDry:
      return 'Oven dry';
    case StateOfMaterial.AirDry:
      return 'Air-dry';
    default:
      return 'Unknown';
  }
};

export const getLabelForPortionPreparationMethod = (preparationMethod: PreparationMethod) => {
  switch (preparationMethod) {
    case PreparationMethod.Moist:
      return 'Moist';
    case PreparationMethod.OvenDry:
      return 'Oven dry';
    case PreparationMethod.AirDry:
      return 'Air-dry';
    case PreparationMethod.PreTreated:
      return 'Pre-treatment';
    default:
      return 'Unknown';
  }
};

/** Creates a selector that returns the number of separations on the given worksheet */
export const createSeparationCountSelector = (createWorksheetSelector: CreateWorksheetSelector) =>
  pipe(createWorksheetSelector!('', false), getNumberOfSeparations);

/**
 * Creates a selector to obtain the label for a portion at the given path, as it is labelled for sieving contexts.
 *
 * Note this is a distinct label from the regular portion label - see createPortionLabelSelector
 */
export const createSievingPortionLabelSelector = (createWorksheetSelector: CreateWorksheetSelector, path: string) =>
  pipe(createSeparationCountSelector(createWorksheetSelector), (totalCount) =>
    getSievingPortionLabelFromPath(path, totalCount)
  );

export const getSievingPortionLabelFromPath = (path: string, totalSeparationCount: number) => {
  if (path.endsWith('.WholePortion') && totalSeparationCount === 0) return 'Sieve Portion';

  let priorSeparationCount = path?.match(/\.Separation\./g)?.length;

  if (isNil(priorSeparationCount) || priorSeparationCount > totalSeparationCount) return 'Unknown';

  if (path.endsWith('.CoarsePortion')) {
    return `${priorSeparationCount === 1 ? 'Coarse ' : ''}Portion${
      totalSeparationCount > 1 ? ` ${priorSeparationCount!}` : ''
    }`;
  }

  if (path.endsWith('.FinePortion.Subspecimen') && totalSeparationCount === priorSeparationCount) return 'Fine Portion';

  return 'Unknown';
};

/**
 * Creates a selector to obtain the label for a portion at the given path.
 *
 * Note this is a distinct label from the label we use in sieving contexts - see createSievingPortionLabelSelector
 */
export const createPortionLabelSelector = (createWorksheetSelector: CreateWorksheetSelector, path: string) =>
  pipe(createSeparationCountSelector(createWorksheetSelector), (totalCount) =>
    getPortionLabelFromPath(path, totalCount)
  );

const getPortionLabelFromPath = (path: string, totalSeparationCount: number) => {
  if (path.endsWith('.WholePortion')) return 'Specimen';

  let priorSeparationCount = path?.match(/\.Separation\./g)?.length;

  if (isNil(priorSeparationCount)) return 'Unknown';

  const separationNumberSuffix =
    priorSeparationCount === 1 && totalSeparationCount === 1 ? '' : ` ${priorSeparationCount!}`;

  if (path.endsWith('.CoarsePortion')) {
    return `Coarse Portion${separationNumberSuffix}`;
  }

  if (path.endsWith('.FinePortion')) {
    return `Fine Portion${separationNumberSuffix}`;
  }

  if (path.endsWith('.FinePortion.Subspecimen')) {
    return `Fine Portion Subspecimen${separationNumberSuffix}`;
  }

  return 'Unknown';
};

/**
 * Currently PSDs always wash on the 75 µm sieve. If this changes replace this with a selector
 * that can obtain this value (from an appropriate place on the model)
 */
export const WashingSieveSizeId: SieveSizeId = '0_075';

/**
 * Creates a selector to obtain the caption of the washing sieve size
 */
export const createWashingSieveSizeCaptionSelector = (createWorksheetSelector: CreateWorksheetSelector) =>
  createSieveSizeCaptionSelector(createWorksheetSelector, WashingSieveSizeId);

/**
 * Creates a selector to obtain the caption of a sieve size with the given id
 *
 * @param createWorksheetSelector
 * @param sieveSizeId
 */
export const createSieveSizeCaptionSelector = (
  createWorksheetSelector: CreateWorksheetSelector,
  sieveSizeId: SieveSizeId
) =>
  pipe(
    createWorksheetSelector!('data.Equipment.AllSieveSizes', false),
    (sieves: ISieveSize[]) => getSieveSizeFromId(sieveSizeId, sieves)!.Caption
  );

export const getSieveSizeFromId = (id: SieveSizeId, sieveSizes: ISieveSize[]) => {
  if (!id) return undefined;
  let sieveSize: ISieveSize | undefined = sieveSizes.find((s: ISieveSize) => s.Id === id);
  if (!sieveSize)
    throw new Error(
      `A sieve size with id ${id} was not found. This sieve size should be available for a worksheet of this type.`
    );
  return sieveSize;
};
